import React from 'react';
import { useSegment } from '@sparknz/journeys-common';
import { useMediaQuery } from '@sparknz/spark-pattern-library';
import { ImpressionRegion, ImpressionW3ddlData } from '@sparknz/tracking';

import { MobilePlanCardsProductsQuery$data } from '__generated__/MobilePlanCardsProductsQuery.graphql';
import { ActiveCategories } from 'components/shop/MobilePlans/typings/category';
import { getTrackingPromoDetails } from 'components/shop/MobilePlans/utils/getPromoDetails';

const PREPAID_CATEGORIES = ['prepaid_retail_extra', 'travel-pack'];

type PlanDetails =
  MobilePlanCardsProductsQuery$data['eligibleProductOffersByCategory']['category']['plans'][0];

type PlanCardImpressionRegionProps = {
  plan: PlanDetails;
  activeCategories: ActiveCategories;
  children?: React.ReactNode;
};

export default function PlanCardImpressionRegion({
  children,
  plan,
  activeCategories,
}: PlanCardImpressionRegionProps) {
  const isLil = useMediaQuery({
    max: 'lil',
  });
  const { isBusiness } = useSegment();
  const isChangePlan = activeCategories.category === 'change_plan';

  const { contractTerm, price, planDetails, planImages, promotions } = plan;
  const isPrepaid = PREPAID_CATEGORIES.includes(activeCategories.category);

  const priceValue = isBusiness
    ? price?.basePriceExcludingTax?.toString()
    : price?.basePrice?.toString();
  const impressionData: ImpressionW3ddlData = {
    event: [
      {
        eventInfo: {
          eventName: 'Component View',
          eventAction: 'component.view',
        },
        attributes: {
          isSuccess: true,
        },
      },
    ],
    component: [
      {
        componentInfo: {
          componentID: `shop.productCards.${
            isPrepaid ? 'prepaidPlans' : 'payMonthlyPlans'
          }`,
          componentName: `Shop ${
            isPrepaid ? 'Prepaid Plans' : 'Pay Monthly Plans'
          }`,
        },
        category: {
          primaryCategory: 'Product',
          subCategory1: 'ServicePlan',
        },
        attributes: {
          isChangePlan,
        },
      },
    ],
    product: [
      {
        productInfo: {
          productID: planDetails?.planId,
          productName: planDetails?.planName,
          manufacturer: 'Spark',
          sku: planDetails?.planId,
          productImage: planImages?.[0]?.thumbnailUrl ?? undefined,
        },
        category: {
          primaryCategory: plan?.dataLayerCategory ?? undefined,
        },
        attributes: {
          displayName: planDetails?.planName,
          itemBasePrice: priceValue,
          itemOneOffCost:
            price?.priceType === 'Recurring' ? '00.00' : priceValue,
          itemRecurringCost:
            price?.priceType === 'Recurring' ? priceValue : '00.00',
          promoMessage:
            getTrackingPromoDetails(promotions).dealText || undefined,
          promoTag:
            getTrackingPromoDetails(promotions).marketingTag || undefined,
          isGSTIncluded: !isBusiness,
          baseStore: isBusiness ? 'businessshop' : 'personalshop',
          paymentType: isPrepaid ? 'Prepaid' : 'Postpaid',
          contractTerm: contractTerm?.name ?? undefined,
          productSpecification: planDetails?.productType ?? undefined,
          segment: isBusiness ? 'BUSINESS' : 'CONSUMER',
          isChangePlan,
          categoryIdentifier: planDetails?.categoryIdentifiers?.join(','),
        },
      },
    ],
  };
  return (
    <ImpressionRegion
      threshold={0.7}
      isFullHeight
      impressionTime={isLil ? 2000 : 1000}
      batchEvent
      impressionData={impressionData}
    >
      {children}
    </ImpressionRegion>
  );
}
